import axios from 'axios';
import apiClient from './client';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Separate client for public consent endpoints — no auth interceptors needed
const publicClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30_000,
});

// ─── Types ───

export interface ConsentFormData {
  member_name: string;
  consent_types: { value: string; label: string }[];
  expires_at: string;
}

export interface MemberConsent {
  id: number;
  type: string;
  method: string;
  consented_at: string;
  expires_at: string | null;
  is_active: boolean;
  document_path: string | null;
  notes: string | null;
  recorder: { id: string; name: string } | null;
  created_at: string;
}

export interface RecordConsentPayload {
  type: string;
  method: string;
  consented_at: string;
  expires_at?: string | null;
  notes?: string;
}

export interface GenerateLinkPayload {
  consent_types: string[];
  expires_in_days?: number;
}

export interface GenerateLinkResponse {
  token: string;
  url: string;
  expires_at: string;
}

// ─── Public endpoints (token-based) ───

export async function getConsentByToken(token: string): Promise<ConsentFormData> {
  const { data } = await publicClient.get(`/consent/${token}`);
  return data.data;
}

export async function submitConsentByToken(
  token: string,
  acceptedTypes: string[],
): Promise<void> {
  await publicClient.post(`/consent/${token}`, {
    accepted_types: acceptedTypes,
  });
}

// ─── Admin endpoints (authenticated) ───

export async function getMemberConsents(memberId: string): Promise<MemberConsent[]> {
  const { data } = await apiClient.get(`/members/${memberId}/consents`);
  return data.data;
}

export async function recordConsent(
  memberId: string,
  payload: RecordConsentPayload,
): Promise<MemberConsent> {
  const { data } = await apiClient.post(`/members/${memberId}/consents`, payload);
  return data.data;
}

export async function withdrawConsent(consentId: number): Promise<void> {
  await apiClient.post(`/consents/${consentId}/withdraw`);
}

export async function generateConsentLink(
  memberId: string,
  payload: GenerateLinkPayload,
): Promise<GenerateLinkResponse> {
  const { data } = await apiClient.post(
    `/members/${memberId}/consents/generate-link`,
    payload,
  );
  return data.data;
}
